import React from 'react';
import ConfusionMatrixPlot from './ConfusionMatrixPlot';
import PerformanceMetricsPlot from './PerformanceMetricsPlot';
import { useTheme } from '../theme/ThemeContext';

const projects = [
	{
		id: 'CHX-001',
		title: 'Chest X-Ray Artifact Removal',
		subtitle: 'CNN-based pneumonia classification with artifact preprocessing',
		repoUrl: 'https://github.com/jarrald/AAI-chest-x-ray',
		problem:
			'Q1 - Does removing non-anatomical artifacts (e.g. the orientation marker R) from chest X-rays improve pneumonia classification performance? Q1.1 - How can the markings be detected and removed reliably and quickly, without damaging the lungs?',
		approach:
			'Built a CNN classification pipeline with OpenCV-based artifact detection and removal. Applied data augmentation, cleaning, and exploratory analysis to address class imbalance. Leveraged transfer learning from pre-trained models to boost performance with limited medical data. Future work includes GRAD-CAM visualization to validate that the model attends to clinically relevant regions.',
		metrics: [
			{ value: 'CNN', label: 'Architecture' },
			{ value: 'Transfer', label: 'Learning' },
			{ value: 'Completed', label: 'Status' },
		],
		techTags: ['Python', 'PyTorch', 'OpenCV', 'CNNs', 'Transfer Learning', 'Medical Imaging', 'GRAD-CAM'],
		tagColors: ['cyan', 'purple', 'pink', 'purple', 'cyan', 'pink', 'purple'],
		image: null,
		imageAlt: '',
	},
	{
		id: 'SYS-002',
		title: 'Shift Management System',
		subtitle: 'Full-stack platform with exhaustive quality assurance & database R&D',
		problem:
			'Enterprise shift scheduling needs transactional integrity under concurrent access. The system required rigorous testing across all layers and a comparative analysis of database paradigms to justify architectural decisions.',
		approach:
			'Designed a Spring Boot + React architecture with MySQL. Testing suite spanned unit, integration, e2e (Playwright), API (Postman), stress/performance (Grafana k6), and user acceptance tests with a separate test database. Database research compared RDBMS (MySQL), document (MongoDB), and graph (Neo4j) models — evaluating indexing, read-replicas, sharding, denormalization, and stored procedures for scheduling logic.',
		metrics: [
			{ value: '6 Test Types', label: 'QA Coverage' },
			{ value: '3 DB Models', label: 'Compared' },
			{ value: 'Completed', label: 'Status' },
		],
		techTags: ['Java', 'Spring Boot', 'React', 'MySQL', 'MongoDB', 'Neo4j', 'Docker', 'Playwright', 'Grafana k6'],
		tagColors: ['purple', 'cyan', 'purple', 'pink', 'cyan', 'purple', 'cyan', 'pink', 'purple'],
		image: null,
		imageAlt: '',
		image2: null,
		image2Alt: '',
	},
	// {
	// 	id: 'ML-003',
	// 	title: 'Reinforcement Learning — Frozen Lake',
	// 	subtitle: 'Q-learning agent solving a stochastic grid-world navigation problem',
	// 	problem:
	// 		'The Frozen Lake environment (Gymnasium) presents a classic reinforcement learning challenge: a 4x4 grid-world where an agent navigates slippery ice to reach a goal without falling through holes. This tests an agent\'s ability to balance exploration vs. exploitation under uncertainty — a core RL problem with real-world parallels in robotics, finance, and autonomous systems.',
	// 	approach:
	// 		'Developed a Q-learning agent with epsilon-greedy exploration, fine-tuning hyperparameters (learning rate, discount factor) to optimize convergence speed and policy quality. Compared performance across deterministic and stochastic environments, analyzing how slippage probability affects Q-value convergence and policy stability. Implemented dynamic epsilon decay to transition from exploration to exploitation, achieving near-optimal navigation while avoiding local minima traps.',
	// 	metrics: [
	// 		{ value: 'Q-Learning', label: 'Algorithm' },
	// 		{ value: '4x4 Grid', label: 'Environment' },
	// 		{ value: 'Completed', label: 'Status' },
	// 	],
	// 	techTags: ['Python', 'Gymnasium', 'Q-Learning', 'Reinforcement Learning', 'OpenAI Gym', 'Epsilon-Greedy', 'Value Iteration', 'Stochastic Environments'],
	// 	tagColors: ['cyan', 'purple', 'cyan', 'purple', 'pink', 'cyan', 'purple', 'pink'],
	// 	image: null,
	// 	imageAlt: '',
	// },
	{
		id: 'SYS-005',
		title: 'Distributed Shift Management Platform',
		subtitle: 'From monolith to event-driven, cloud-native architecture — B.Sc. finals project (in progress)',
		problem:
			'The tested shift-management monolith cannot scale its parts independently, survive component failures, or absorb load spikes during shift-heavy periods — so the finals project re-architects it as a distributed system built for elasticity, high availability, and low latency.',
		approach:
			'Decomposing the platform into 5 polyglot microservices (Spring Boot, FastAPI, Express.js), each owning its data. Services communicate asynchronously via a message broker with idempotent handlers; consistency across services uses the Saga pattern, eventual consistency, and CQRS for scheduling queries. An API gateway exposes REST + GraphQL behind JWT/OAuth2. docker-compose drives development, while local Kubernetes with replicas, load balancing, autoscaling, and failover drills simulates production — with CI/CD throughout and a local-LLM AI assistant negotiating shift swaps.',
		metrics: [
			{ value: '5', label: 'Microservices' },
			{ value: 'Saga · CQRS', label: 'Core Patterns' },
			{ value: 'In Progress', label: 'Status' },
		],
		techTags: ['Kubernetes', 'Docker', 'RabbitMQ', 'Microservices', 'Saga & CQRS', 'Eventual Consistency', 'REST + GraphQL', 'JWT / OAuth2', 'Local LLM'],
		tagColors: ['cyan', 'purple', 'pink', 'purple', 'cyan', 'purple', 'pink', 'cyan', 'purple'],
		image: null,
		imageAlt: '',
	},
	{
		id: 'DATA-004',
		title: 'Production Data Pipeline Engineering',
		subtitle: 'End-to-end data ingestion, validation, and visualization at scale — Voonix.net',
		problem:
			'A data platform supporting 4,000+ external systems required reliable daily ingestion of thousands of data points, with strict validation before reaching dashboards — all while running on shared hosting with tight performance constraints.',
		approach:
			'Built and extended ingestion pipelines processing data from 4,000+ external brands and systems. Implemented rule-based anomaly detection that flagged invalid data before reaching dashboards — eliminating manual QA overhead. Developed interactive ECharts dashboards with DataTables for sortable, filterable stakeholder reporting. Optimized cron-based ingestion jobs through runtime profiling and memory tuning, with database indexing and query optimization that maintained fast reporting as data volume scaled.',
		metrics: [
			{ value: '4,000+', label: 'External Systems' },
			{ value: 'PHP+MySQL', label: 'Stack' },
			{ value: '4 Years', label: 'Production Runtime' },
		],
		techTags: ['PHP', 'MySQL', 'JavaScript', 'ECharts', 'DataTables', 'Web Scraping', 'Data Validation', 'Cron'],
		tagColors: ['purple', 'cyan', 'pink', 'purple', 'cyan', 'pink', 'purple', 'cyan'],
		image: null,
		imageAlt: '',
	},
];

function Projects() {
	const { theme } = useTheme();
	const isDark = theme === 'dark';

	return (
		<section className="projects-section section" id="projects">
			<div className="container">
				<div className="section-header reveal">
					<span className="section-label">{isDark ? '# 04. PROJECTS' : '04 — Projects'}</span>
					<h2 className="section-header__title">{isDark ? 'Model Registry' : 'Selected Projects'}</h2>
					<p className="section-header__subtitle">
						{isDark
							? 'Projects framed as model cards — focusing on problem architecture, trade-off analysis, and measurable outcomes.'
							: 'Selected work framed as case studies — problem architecture, trade-offs, and measurable outcomes.'}
					</p>
				</div>

				<div className="grid-2">
					{projects.map((proj, idx) => (
						<div key={proj.id} className={`model-card reveal reveal-delay-${idx + 1}`}>
							<div className="model-card__header">
								<p className="model-card__id">{proj.id}</p>
								<h3 className="model-card__title">{proj.title}</h3>
								<p className="model-card__subtitle">{proj.subtitle}</p>
							</div>
							<div className="model-card__body">
								<p className="model-card__section-label">Problem Statement</p>
								<p className="model-card__text">{proj.problem}</p>
								<p className="model-card__section-label">Approach</p>
								<p className="model-card__text">{proj.approach}</p>
								<p className="model-card__section-label">Key Metrics</p>
								<div className="model-card__metrics">
									{proj.metrics.map((m) => (
										<div className="model-card__metric" key={m.label}>
											<div className="model-card__metric-value">{m.value}</div>
											<div className="model-card__metric-label">{m.label}</div>
										</div>
									))}
								</div>
								{proj.id === 'CHX-001' ? (
									<ConfusionMatrixPlot />
								) : proj.id === 'SYS-002' ? (
									<PerformanceMetricsPlot />
								) : proj.image ? (
									<img
										src={proj.image}
										alt={proj.imageAlt}
										style={{
											width: '100%',
											marginTop: '16px',
											borderRadius: '8px',
											border: '1px solid var(--glass-border)',
										}}
									/>
								) : null}
								{proj.image2 && (
									<img
										src={proj.image2}
										alt={proj.image2Alt}
										style={{
											width: '100%',
											marginTop: '12px',
											borderRadius: '8px',
											border: '1px solid var(--glass-border)',
										}}
									/>
								)}
							</div>
							<div className="model-card__footer">
								{proj.techTags.map((tag, i) => (
									<span key={tag} className={`tech-tag ${proj.tagColors[i] ? `tech-tag--${proj.tagColors[i]}` : ''}`}>
										{tag}
									</span>
								))}
								{proj.repoUrl && (
									<a
										className="model-card__repo"
										href={proj.repoUrl}
										target="_blank"
										rel="noopener noreferrer"
									>
										View on GitHub →
									</a>
								)}
							</div>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}

export default Projects;